/**
 * The Smart Pen's shape recogniser (SRS C5).
 *
 * A stroke comes in, one of the ten shapes in the library comes out — or nothing,
 * which is the answer that matters most: a shape only snaps when the maths is sure.
 *
 * SRS C5.1 requires this to sit behind one interface with one tunable number, so
 * that the algorithm can be improved, or thrown away entirely, without the pen
 * engine noticing. Nothing outside this file knows how the scoring works.
 */

import type { ShapeMatch, ShapeRecogniser } from '@opal/contracts';
import type { Point } from '@opal/shared';

import { scoreArrow, scoreEllipse, scoreLine } from './recognise-curves.js';
import { measureStroke } from './recognise-geometry.js';
import { scorePolygons } from './recognise-polygons.js';

/**
 * How sure the recogniser has to be before a shape snaps.
 *
 * SRS K3 puts this one number in Admin Settings; step 5.4 wires it up. Raising it
 * means fewer snaps and fewer surprises, lowering it means the opposite.
 */
export const DEFAULT_SHAPE_CONFIDENCE = 0.7;

/**
 * Every shape this stroke could be, most specific first.
 *
 * Order settles ties only: a drawn square scores the same as a rectangle, and the
 * squarer answer is the one the teacher meant.
 */
function candidatesFor(points: readonly Point[]): ShapeMatch[] {
  const path = measureStroke(points);
  if (!path) return [];

  const candidates: ShapeMatch[] = [...scorePolygons(path)];
  const ellipse = scoreEllipse(path);
  if (ellipse) candidates.push(ellipse);
  const arrow = scoreArrow(path);
  if (arrow) candidates.push(arrow);
  const line = scoreLine(path);
  if (line) candidates.push(line);
  return candidates;
}

/** The best match at or above the threshold, or null when nothing is sure enough. */
export function recogniseShape(points: readonly Point[], threshold: number): ShapeMatch | null {
  let best: ShapeMatch | null = null;
  for (const candidate of candidatesFor(points)) {
    if (candidate.confidence < threshold) continue;
    if (!best || candidate.confidence > best.confidence) best = candidate;
  }
  return best;
}

export function createShapeRecogniser(): ShapeRecogniser {
  return {
    recognise: (points, threshold) => recogniseShape(points, threshold),
  };
}
